import { useState } from "react";
import { ChevronRight, ChevronDown, MessageCircle, BarChart3, Star, CheckCircle2, Zap, ArrowRight, Building2, FileText, Info, Moon, Sunrise, Sun, Clock, TrendingDown, AlertTriangle, ThumbsUp, CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { LINE_URL, EARLYBIRD_TOTAL, EARLYBIRD_LEFT, LIGHT_TRIAL_LEFT } from "../constants";
import { PlanRecommender } from "./PlanRecommender";
import { TeamValueSection } from "./TeamValueBlock";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const TIP_CS21 = "服務時段：週一～五 10:00–21:00；週六、日 10:00–20:00";
const TIP_CS23 = "服務時段：週一～五 10:00–23:00；週六、日 10:00–20:00";

const InfoTip = ({ text }) => (
  <Tooltip delayDuration={80}>
    <TooltipTrigger asChild>
      <button
        type="button"
        data-testid="feat-info"
        aria-label="更多說明"
        className="inline-flex items-center align-middle ml-1 -translate-y-px text-slate-400 hover:text-[#0A2540] transition-colors"
      >
        <Info className="w-3.5 h-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[220px] bg-[#0A2540] text-white text-xs leading-relaxed">
      {text}
    </TooltipContent>
  </Tooltip>
);

const Feat = ({ text, bold, star, moon, tip, sub, accent }) => (
  <div className="flex items-start gap-3">
    {star ? (
      <Star className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: accent }} />
    ) : moon ? (
      <Moon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: accent }} />
    ) : (
      <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: accent }} />
    )}
    <span className={`${bold ? "text-[#0A2540] font-semibold" : "text-[#475569]"} text-sm md:text-base`}>
      {text}
      {tip && <InfoTip text={tip} />}
      {sub && <span className="block text-[11px] md:text-xs font-bold mt-0.5" style={{ color: accent }}>{sub}</span>}
    </span>
  </div>
);

const lightStats = [{ v: "20 隻", l: "保障影片" }, { v: "15 位+", l: "KOL 導流" }, { v: "25%", l: "訂單抽成" }];
const growthStats = [{ v: "48 隻", l: "保障影片" }, { v: "40 位+", l: "KOL 導流" }, { v: "20%", l: "訂單抽成" }];
const flagshipStats = [{ v: "60 隻", l: "保障影片" }, { v: "55 位+", l: "KOL 導流" }, { v: "20%", l: "訂單抽成" }];

// ===== 開場痛點區塊：不做行銷會少賺多少（Loss Framing） =====
const PainOpen = () => (
  <div data-testid="pain-open" className="mb-10 md:mb-12">
    <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-3xl p-6 md:p-10 shadow-xl relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-64 h-64 bg-red-500/10 rounded-full blur-3xl"></div>
      <div className="relative">
        <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/40 text-red-300 px-3 py-1 rounded-full text-xs font-black mb-4">
          <TrendingDown className="w-3.5 h-3.5" />
          不做行銷，一年少賺多少？
        </div>
        <h3 className="text-2xl md:text-4xl font-black text-white leading-tight mb-3">
          包棟業主一年<span className="text-red-400">平均少賺 NT$ 240 萬</span>
        </h3>
        <p className="text-sm md:text-base text-white/70 mb-6 leading-relaxed max-w-3xl">
          自營或外包散做的業主，最常見的三大隱形損失 — 每一項都在悄悄吃掉您的營收。
        </p>
        <div className="grid md:grid-cols-3 gap-3 md:gap-4">
          {[
            { icon: <TrendingDown className="w-5 h-5" />, ttl: "平日住房率低", loss: "月損 NT$ 8–12 萬", desc: "只靠 OTA 排名，週間空房沒人填" },
            { icon: <AlertTriangle className="w-5 h-5" />, ttl: "沒有 KOL 曝光", loss: "月損 NT$ 5–8 萬", desc: "被同業搶走新客詢問流量" },
            { icon: <Clock className="w-5 h-5" />, ttl: "客服接不完", loss: "月損 NT$ 3–5 萬", desc: "晚上與尖峰時段訂單流失" },
          ].map((p) => (
            <div key={p.ttl} className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2 text-red-400">
                {p.icon}
                <span className="text-sm md:text-base font-bold text-white">{p.ttl}</span>
              </div>
              <div className="text-lg md:text-xl font-black text-red-400 mb-1">{p.loss}</div>
              <div className="text-xs text-white/60 leading-snug">{p.desc}</div>
            </div>
          ))}
        </div>
        <div className="mt-5 md:mt-6 text-sm md:text-base text-white/80 leading-relaxed">
          <span className="text-[#F5C24A] font-bold">好消息：</span>Lakeze 一條龍整合方案的成本，只占其中的
          <span className="text-[#F5C24A] font-black text-lg mx-1">約 30%</span>
          — 用最省的方式一次補齊所有漏洞。
        </div>
      </div>
    </div>
  </div>
);

// ===== 現場痛點勾選表（勾越多越推成長） =====
const PainChecker = () => {
  const pains = [
    "訂單來源不穩，OTA 一沒排名就沒單",
    "沒時間管 IG／FB／LINE@ 社群",
    "客服訊息接不完，晚上尖峰全流失",
    "平日住房率低，週末爆滿卻補不了",
    "不知道怎麼定價，房價開高開低都虧",
    "想拍影片、找 KOL 但不知從何開始",
  ];
  const [checked, setChecked] = useState([]);
  const toggle = (i) => setChecked((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
  const n = checked.length;

  const rec =
    n === 0 ? null :
    n <= 2 ? { name: "輕量方案", note: "痛點不多，可先從輕量方案試起 — 但月營收超過 40 萬時建議升級。", color: "#0284C7" } :
    n <= 4 ? { name: "人客多成長方案", note: "您的痛點正是人客多成長方案的甜蜜點 — 一次補齊所有漏洞、8 成業主的選擇。", color: "#0D9488" } :
    { name: "人客多成長方案（或旗艦）", note: "痛點多，強烈建議人客多成長方案；若月營收已 90 萬以上、多物件，可再升級旗艦。", color: "#0D9488" };

  return (
    <div data-testid="pain-checker" className="max-w-3xl mx-auto mb-10 md:mb-12">
      <div className="bg-white border-2 border-slate-200 rounded-3xl p-5 md:p-7 shadow-md">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <div className="inline-flex items-center gap-2 bg-[#0D9488] text-white px-3 py-1 rounded-full text-xs font-black">
            <ThumbsUp className="w-3.5 h-3.5" />
            現場勾選：您有哪些痛點？
          </div>
          <span className="text-[11px] md:text-xs text-[#475569]">勾選越多，越適合人客多成長方案</span>
        </div>
        <h3 className="text-lg md:text-xl font-black text-[#0A2540] mb-4">請對照現況，勾選您正在遇到的問題</h3>

        <div className="grid md:grid-cols-2 gap-2 mb-4">
          {pains.map((p, i) => {
            const isOn = checked.includes(i);
            return (
              <button
                key={i}
                type="button"
                onClick={() => toggle(i)}
                data-testid={`pain-item-${i}`}
                className={`text-left flex items-start gap-3 px-3 py-2.5 rounded-xl border-2 transition-all ${
                  isOn ? "bg-[#0D9488]/10 border-[#0D9488]" : "bg-white border-slate-200 hover:border-[#0D9488]/40"
                }`}
              >
                <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                  isOn ? "bg-[#0D9488] border-2 border-[#0D9488]" : "bg-white border-2 border-slate-300"
                }`}>
                  {isOn && <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} />}
                </div>
                <span className={`text-sm leading-snug ${isOn ? "font-bold text-[#0A2540]" : "text-[#475569]"}`}>{p}</span>
              </button>
            );
          })}
        </div>

        {n > 0 && rec && (
          <div
            data-testid="pain-checker-result"
            className="rounded-2xl px-4 py-3 border-2 flex items-start gap-3"
            style={{ background: `${rec.color}0F`, borderColor: rec.color }}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-black text-white" style={{ background: rec.color }}>
              {n}/{pains.length}
            </div>
            <div className="flex-1">
              <div className="text-sm md:text-base font-black" style={{ color: rec.color }}>
                您有 {n} 個痛點 — 建議 <span className="underline decoration-2 underline-offset-2">{rec.name}</span>
              </div>
              <div className="text-xs md:text-sm text-[#475569] mt-0.5 leading-snug">{rec.note}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== 定位標籤 =====
const PositionTag = ({ text, mode, color }) => {
  // mode: "fit" (適合) | "star" (最多業主) | "unfit" (不適合誰)
  const cfg = {
    fit:   { bg: "#F0FDFA", border: color, icon: <CheckCircle2 className="w-3.5 h-3.5" />, iconColor: color },
    star:  { bg: "#FFF7ED", border: "#FF6B00", icon: <Star className="w-3.5 h-3.5" />, iconColor: "#FF6B00" },
    unfit: { bg: "#F1F5F9", border: "#CBD5E1", icon: <AlertTriangle className="w-3.5 h-3.5" />, iconColor: "#94A3B8" },
  }[mode];
  return (
    <div className="rounded-lg border px-2.5 py-1.5 mb-6 flex items-start gap-1.5" style={{ background: cfg.bg, borderColor: cfg.border }}>
      <span className="flex-shrink-0 mt-0.5" style={{ color: cfg.iconColor }}>{cfg.icon}</span>
      <span className={`text-[11px] md:text-xs leading-snug ${mode === "unfit" ? "text-[#64748B]" : "text-[#0A2540] font-semibold"}`}>{text}</span>
    </div>
  );
};

// ===== 客服黃金時段真實數據洞察（放主卡片與比較表之間） =====
const PrimeTimeInsight = () => {
  // 四段時段對比（來源：實際客服系統進訊資料）
  const buckets = [
    { key: "night",  label: "深夜 03–05",   sub: "多數同業都在服務", pct: 1,  color: "#94A3B8", icon: <Moon className="w-4 h-4" /> },
    { key: "offpk",  label: "下午 15–17",   sub: "一般日間非高峰",   pct: 5,  color: "#0EA5E9", icon: <Sun className="w-4 h-4" /> },
    { key: "day",    label: "白天 12–14",   sub: "一般日間高峰",     pct: 13, color: "#0284C7", icon: <Sun className="w-4 h-4" /> },
    { key: "prime",  label: "晚上 21–23",   sub: "新客首次詢問黃金時段", pct: 20, color: "#FF6B00", icon: <Sunrise className="w-4 h-4" />, highlight: true },
  ];
  const max = 22; // scale
  return (
    <div data-testid="prime-time-insight" className="mb-12 bg-gradient-to-br from-[#FFF7ED] via-white to-[#FFF7ED] border-2 border-[#FF6B00]/40 rounded-3xl p-5 md:p-8 shadow-md">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-3 py-1 rounded-full text-xs font-black">
          <Clock className="w-3.5 h-3.5" />
          真實客服進訊數據
        </div>
        <span className="text-[11px] md:text-xs text-[#475569]">
          來源：<span className="font-bold text-[#0A2540]">森山寫照 × 哈庫納瑪塔塔</span> 客服系統實測，180 天 <span className="font-bold text-[#0A2540]">8,056 筆</span>進訊
        </span>
      </div>
      <h3 className="text-xl md:text-2xl font-black text-[#0A2540] mb-1 leading-tight">
        晚上 <span className="text-[#FF6B00]">21:00–23:00</span> 是新客首次詢問的<span className="text-[#FF6B00]">黃金時段</span>
      </h3>
      <p className="text-sm text-[#475569] mb-6 leading-relaxed">
        平日近 <span className="font-black text-[#FF6B00]">19%</span>、假日高達 <span className="font-black text-[#FF6B00]">21%</span> 的新客首次進訊落在這 3 小時內 —
        比深夜低谷高出約 <span className="font-black text-[#FF6B00]">15–20 倍</span>，卻是絕大多數同業客服打烊的時段。
      </p>

      {/* 水平條狀對比圖 */}
      <div className="space-y-3 mb-5" data-testid="prime-time-chart">
        {buckets.map((b) => (
          <div key={b.key} className="flex items-center gap-2 md:gap-3">
            <div className="w-[92px] md:w-[128px] flex items-center gap-1.5 flex-shrink-0" style={{ color: b.color }}>
              {b.icon}
              <div>
                <div className="text-xs md:text-sm font-bold leading-tight">{b.label}</div>
                <div className="text-[10px] md:text-[11px] text-[#475569] font-normal leading-tight">{b.sub}</div>
              </div>
            </div>
            <div className="flex-1 h-7 md:h-8 bg-white border border-slate-200 rounded-full overflow-hidden relative shadow-inner">
              <div
                className="h-full rounded-full transition-all flex items-center justify-end pr-3"
                style={{
                  width: `${(b.pct / max) * 100}%`,
                  background: b.highlight
                    ? "linear-gradient(90deg, #FFB86B 0%, #FF6B00 100%)"
                    : b.color,
                  boxShadow: b.highlight ? "0 4px 14px rgba(255,107,0,0.35)" : "none",
                }}
              >
                {b.pct >= 10 && (
                  <span className="text-xs md:text-sm font-black text-white drop-shadow">
                    {b.pct}%
                  </span>
                )}
              </div>
              {b.pct < 10 && (
                <span className="absolute inset-y-0 flex items-center text-xs font-black" style={{ left: `calc(${(b.pct / max) * 100}% + 6px)`, color: b.color }}>
                  {b.pct}%
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 三方案覆蓋結論 */}
      <div className="grid grid-cols-3 gap-2 md:gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-2 md:p-3 text-center">
          <div className="text-[10px] md:text-xs font-bold text-[#64748B] mb-1">輕量方案</div>
          <div className="text-xs md:text-sm font-black text-[#94A3B8]">錯過黃金 3h</div>
        </div>
        <div className="bg-gradient-to-br from-[#F0FDFA] to-[#CCFBF1] border-2 border-[#0D9488] rounded-xl p-2 md:p-3 text-center shadow-md">
          <div className="text-[10px] md:text-xs font-black text-[#0D9488] mb-1">人客多成長方案</div>
          <div className="text-xs md:text-sm font-black text-[#0A2540]">✓ 完整接住</div>
        </div>
        <div className="bg-gradient-to-br from-[#FFF7ED] to-[#FFE4C4] border-2 border-[#FF6B00] rounded-xl p-2 md:p-3 text-center shadow-md">
          <div className="text-[10px] md:text-xs font-black text-[#FF6B00] mb-1">旗艦方案</div>
          <div className="text-xs md:text-sm font-black text-[#0A2540]">✓ 完整接住</div>
        </div>
      </div>
      <p className="text-[11px] text-[#475569] mt-3 text-center leading-snug">
        ＊每 5 位新客約有 1 位在 21–23 首次詢問，晚 2 小時打烊 =<span className="font-bold text-[#FF6B00]"> 直接接住這批訂單</span>
      </p>
      <div className="text-[10px] md:text-[11px] text-[#94A3B8] mt-3 pt-3 border-t border-[#FF6B00]/20 leading-relaxed">
        <div className="font-bold text-[#64748B] mb-1">資料統計範圍</div>
        <div>‧ 哈庫納瑪塔塔：2026/04/26 14:07 – 2026/08/13 13:17</div>
        <div>‧ 森山寫照：2025/07/18 16:57 – 2026/08/12 16:48</div>
        <div>‧ 合計 180 天內 8,056 筆客服系統實際進訊，依時段分布計算</div>
      </div>
    </div>
  );
};

const StatStrip = ({ stats, accent }) => (
  <div className="mb-5">
    <div className="grid grid-cols-3 gap-2">
      {stats.map((s) => (
        <div key={s.l} className="rounded-xl border p-2 text-center" style={{ borderColor: `${accent}33`, background: `${accent}0D` }}>
          <div className="text-sm md:text-base font-black leading-tight" style={{ color: accent }}>{s.v}</div>
          <div className="text-[10px] text-[#475569] mt-0.5">{s.l}</div>
        </div>
      ))}
    </div>
  </div>
);

// ===== 精簡方案卡（共用元件）=====
const PLAN_BASE = [
  { t: "廣告投放全包" },
  { t: "OTA 代操" },
  { t: "包棟 PMS" },
  { t: "專屬客戶經理" },
  { t: "會員轉單" },
];

const BaseChips = ({ accent }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 mb-5" data-testid="plan-base-chips">
    <div className="text-[10px] font-black text-slate-400 tracking-wide mb-1.5">三方案都包含</div>
    <div className="flex flex-wrap gap-x-2.5 gap-y-1">
      {PLAN_BASE.map((b) => (
        <span key={b.t} className={`inline-flex items-center gap-1 text-[11px] md:text-xs ${b.hl ? "font-black text-[#FF6B00]" : "font-medium text-[#0A2540]"}`}>
          {b.hl ? <Star className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" style={{ color: accent }} />}
          {b.t}
        </span>
      ))}
    </div>
  </div>
);

const PlanCard = ({ id, accent, gradient, badge, limited, title, position, positionMode, price, original, saveTag, stats, feats, note, extra, popular, cta }) => (
  <div
    data-testid={id}
    className={`relative bg-white rounded-3xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-2 ${
      popular ? "shadow-xl ring-2 ring-[#0D9488] lg:scale-[1.02]" : "shadow-lg border border-slate-200"
    }`}
  >
    {popular && (
      <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-[#0A2540] text-white text-[10px] md:text-xs font-black px-4 py-1 rounded-b-xl z-10 tracking-wider">
        熱門推薦 · 80% 業主選擇
      </div>
    )}
    <div className={`px-5 md:px-6 py-3.5 ${popular ? "pt-6" : ""} text-white flex items-center justify-between gap-2 flex-wrap`} style={{ background: gradient }}>
      <span className="inline-block bg-white text-[10px] md:text-xs font-bold px-3 py-1 rounded-full shadow-sm" style={{ color: accent }}>{badge}</span>
      {limited && (
        <span className="inline-flex items-center gap-1.5 bg-red-500/25 border border-white/40 rounded-full px-2.5 py-1">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
          </span>
          <span className="text-[10px] md:text-xs font-bold text-white">{limited}</span>
        </span>
      )}
    </div>

    <div className="p-6 md:p-7 flex flex-col flex-1">
      <h3 className="text-xl md:text-2xl font-black text-[#0A2540] mb-3">{title}</h3>
      <PositionTag mode={positionMode} color={accent} text={position} />

      <div className="flex items-end gap-2 flex-wrap mb-1">
        <div className="flex items-baseline gap-1">
          <span className="text-sm text-[#475569]">NT$</span>
          <span className="text-4xl md:text-5xl font-black text-[#0A2540]">{price}</span>
          <span className="text-sm text-[#475569]">/ 月</span>
        </div>
        {original && <span className="text-sm text-slate-400 line-through mb-1.5">NT$ {original}</span>}
        {saveTag && <span className="text-[11px] font-black text-white rounded-full px-2 py-0.5 mb-2" style={{ background: accent }}>{saveTag}</span>}
      </div>
      <div className="text-xs text-[#475569] mb-5 flex items-center gap-1">
        <Zap className="w-3.5 h-3.5" style={{ color: accent }} />
        首次啟動費 <span className="font-black text-[#0A2540]">NT$ 50,000</span>（一次）
        <InfoTip text="含系統開通、社群建置、SEO 設定、團隊配置導入，僅收取一次" />
      </div>

      <StatStrip stats={stats} accent={accent} />
      <BaseChips accent={accent} />

      <div className="space-y-3 mb-5">
        {feats.map((f, i) => <Feat key={i} {...f} accent={accent} />)}
      </div>

      {extra}
      {note && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-5 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div className="text-[11px] text-slate-500 leading-snug">{note}</div>
        </div>
      )}

      <a
        href={LINE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={cta}
        className="mt-auto w-full flex items-center justify-center gap-2 py-4 rounded-full font-bold text-white transition-all shadow-lg"
        style={{ background: accent }}
      >
        <MessageCircle className="w-5 h-5" />立即諮詢
      </a>
    </div>
  </div>
);

const lightDiff = [
  { text: "社群小編經營（IG／FB／LINE@）", bold: true, star: true },
  { text: "客單價提升方案包裝設計", bold: true, star: true, sub: "平均提升 10–25% 客單價，已有多個成功見證" },
  { text: "客服接單 10:00–21:00", tip: TIP_CS21 },
  { text: "基礎庫存與定價建議" },
  { text: "Google 評價優化（個人用）" },
];
const growthDiff = [
  { text: "社群小編經營（IG／FB／LINE@）", bold: true, star: true },
  { text: "客單價提升方案包裝設計", bold: true, star: true, sub: "平均提升 10–25% 客單價，已有多個成功見證" },
  { text: "客服接單延長至 23:00，接住夜間新客高峰", bold: true, moon: true, tip: TIP_CS23 },
  { text: "庫存與定價優化（隨季節動態調整）", bold: true },
  { text: "每月進單客戶分析報告", bold: true },
  { text: "Google 評價優化（個人用）" },
];
const flagshipDiff = [
  { text: "社群小編經營（IG／FB／LINE@）", bold: true, star: true },
  { text: "客單價提升方案包裝設計", bold: true, star: true, sub: "平均提升 10–25% 客單價，已有多個成功見證" },
  { text: "客服接單延長至 23:00，接住夜間新客高峰", bold: true, moon: true, tip: TIP_CS23 },
  { text: "多棟庫存統一調度與定價優化（跨棟導流）", bold: true },
  { text: "KOL 與拍攝檔期優先排程", bold: true },
  { text: "每月進單客戶分析報告＋競品房價監測", bold: true },
  { text: "Google 評價優化（團隊用）" },
];

const FlagshipMultiUnit = () => (
  <div className="rounded-xl border border-[#FF6B00]/30 bg-[#FFF7ED] px-4 py-3 mb-5" data-testid="flagship-multi-unit">
    <div className="flex items-center gap-2 mb-1">
      <Building2 className="w-4 h-4 text-[#FF6B00]" />
      <span className="text-xs md:text-sm font-black text-[#0A2540]">棟數越多、庫存越高，越划算</span>
    </div>
    <div className="text-[11px] md:text-xs text-[#475569] leading-snug">
      第 2 棟起每棟只加 <span className="font-black text-[#FF6B00]">NT$ 5,000</span>，整組團隊、廣告與 KOL 共用。
    </div>
  </div>
);

// 三方案完整比較（輕量 / 成長 / 旗艦）
const CMP_ROWS = [
  ["月費", "NT$38,000", "NT$66,000→56,000", "NT$108,000→78,000"],
  ["首次啟動費", "NT$50,000", "NT$50,000", "NT$50,000"],
  ["訂單抽成", "25%", "20%", "20%"],
  ["廣告投手＋素材製作（廣告費全包）", "y", "y", "y"],
  ["社群小編經營（IG／FB／LINE@）", "y", "y", "y"],
  ["OTA 上架管理與代操", "y", "y", "y"],
  ["客服時段", "10:00–21:00", "10:00–23:00", "10:00–23:00"],
  ["夜間 21–23 新客覆蓋", "night-no", "night-yes", "night-yes"],
  ["保障影片", "20 隻", "48 隻", "60 隻"],
  ["影片保障價值", "約 20 萬", "約 48 萬", "約 60 萬"],
  ["KOL 導流", "15 位以上", "40 位以上", "55 位以上"],
  ["客單價提升方案包裝設計", "y", "y", "y"],
  ["客戶經理・庫存與定價優化", "基礎", "y", "y"],
  ["每月進單客戶分析報告", "n", "y", "y"],
  ["多棟庫存統一調度／跨棟導流", "n", "n", "y"],
  ["競品房價監測", "n", "n", "y"],
  ["Google 評價優化", "個人用", "個人用", "團隊用"],
  ["人客多包棟 PMS 系統", "y", "y", "y"],
  ["人客多會員轉單訂購", "y", "y", "y"],
];

const HL_ROWS = ["社群小編經營（IG／FB／LINE@）", "客單價提升方案包裝設計"];

const cmpCell = (v, accent = "#FF6B00") =>
  v === "y" ? (
    <CheckCircle2 className="w-4 h-4 text-green-600 inline-block" />
  ) : v === "n" ? (
    <span className="text-slate-300">—</span>
  ) : v === "night-yes" ? (
    <span className="inline-flex items-center gap-1 font-black" style={{ color: accent }}>
      <Moon className="w-3.5 h-3.5" /> 完整接單
    </span>
  ) : v === "night-no" ? (
    <span className="inline-flex items-center gap-1 text-slate-400">
      <Moon className="w-3.5 h-3.5 opacity-40" /> 未涵蓋
    </span>
  ) : (
    <span>{v}</span>
  );

const ComparisonTable = () => (
  <div className="mb-12" data-testid="plan-comparison">
    <div className="text-center mb-5">
      <h3 className="text-xl md:text-2xl font-bold text-[#0A2540]">三方案完整比較</h3>
      <p className="text-sm text-[#475569] mt-1">一眼看懂人客多成長方案與旗艦方案多了什麼</p>
    </div>
    <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
      <table className="w-full min-w-[560px] text-sm border-collapse" data-testid="plan-comparison-table">
        <thead>
          <tr>
            <th className="text-left px-4 py-3 bg-[#0A2540] text-white font-bold sticky left-0 z-10">項目</th>
            <th className="px-3 py-3 bg-[#0284C7] text-white font-bold text-center whitespace-nowrap">輕量</th>
            <th className="px-3 py-3 bg-[#0D9488] text-white font-bold text-center whitespace-nowrap">人客多成長</th>
            <th className="px-3 py-3 bg-[#FF6B00] text-white font-bold text-center whitespace-nowrap">旗艦</th>
          </tr>
        </thead>
        <tbody>
          {CMP_ROWS.map((r, i) => {
            const rowBg = i % 2 ? "bg-[#F8FAFC]" : "bg-white";
            const isCs = r[0] === "客服時段";
            const isNight = r[0] === "夜間 21–23 新客覆蓋";
            return (
              <tr key={r[0]} className={rowBg}>
                <td className={`px-4 py-2.5 sticky left-0 z-10 whitespace-nowrap ${rowBg} ${HL_ROWS.includes(r[0]) ? "font-black text-[#FF6B00]" : "font-medium text-[#0A2540]"}`}>
                  {HL_ROWS.includes(r[0]) && <Star className="w-3.5 h-3.5 inline-block mr-1 -translate-y-px" />}{r[0]}
                </td>
                <td className="px-3 py-2.5 text-center text-[#475569]">{cmpCell(r[1])}</td>
                {isCs ? (
                  <td className="px-3 py-2.5 text-center bg-[#F0FDFA]">
                    <span className="font-black text-[#0D9488]">{r[2]}</span>
                    <span className="block mt-0.5 mx-auto w-fit text-[9px] font-bold text-white bg-[#0D9488] rounded-full px-1.5 py-0.5">含夜間 21–23 接單</span>
                  </td>
                ) : isNight ? (
                  <td className="px-3 py-2.5 text-center bg-[#F0FDFA]">
                    <span className="inline-flex items-center gap-1 text-[#0D9488] font-black text-sm">
                      <Moon className="w-4 h-4" /> 完整接單
                    </span>
                    <span className="block mt-0.5 mx-auto w-fit text-[9px] font-bold text-white bg-[#0D9488] rounded-full px-1.5 py-0.5">新客詢問高峰 ≈19–21%</span>
                  </td>
                ) : (
                  <td className="px-3 py-2.5 text-center text-[#475569]">{cmpCell(r[2], "#0D9488")}</td>
                )}
                {isCs ? (
                  <td className="px-3 py-2.5 text-center bg-[#FFF7ED]">
                    <span className="font-black text-[#FF6B00]">{r[3]}</span>
                    <span className="block mt-0.5 mx-auto w-fit text-[9px] font-bold text-white bg-[#FF6B00] rounded-full px-1.5 py-0.5">含夜間 21–23 接單</span>
                  </td>
                ) : isNight ? (
                  <td className="px-3 py-2.5 text-center bg-[#FFF7ED]">
                    <span className="inline-flex items-center gap-1 text-[#FF6B00] font-black text-sm">
                      <Moon className="w-4 h-4" /> 完整接單
                    </span>
                    <span className="block mt-0.5 mx-auto w-fit text-[9px] font-bold text-white bg-[#FF6B00] rounded-full px-1.5 py-0.5">新客詢問高峰 ≈19–21%</span>
                  </td>
                ) : (
                  <td className="px-3 py-2.5 text-center text-[#0A2540] font-semibold bg-[#FFF7ED]">{cmpCell(r[3])}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <p className="text-xs text-slate-400 mt-2 text-center">＊客服時段為週一至五；週六、日 10:00–20:00。影片依實際情況調整，避免影響營收，不一定拍滿。</p>
  </div>
);

const PricingSection = () => {
  const [flexOpen, setFlexOpen] = useState(false);
  return (
    <TooltipProvider>
      <section data-testid="pricing-section" id="pricing" className="py-14 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-12">
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-flex items-center gap-2 bg-[#FF6B00]/10 text-[#FF6B00] px-4 py-2 rounded-full text-sm font-bold mb-5 md:mb-6">
              <BarChart3 className="w-4 h-4" />
              方案價格
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-[#0A2540] tracking-tight mb-4">
              選擇最適合您的合作方式
            </h2>
            <p className="text-base md:text-lg text-[#475569] max-w-2xl mx-auto px-2">
              入門、中階、高階三階梯方案，滿足不同階段與規模的民宿業主需求
            </p>
          </div>

          {/* 開場痛點：不做行銷少賺多少 */}
          <PainOpen />

          {/* 方案推薦選擇器 */}
          <PlanRecommender />

          {/* 現場勾選：您有哪些痛點？ */}
          <PainChecker />

          {/* Contract Download CTA */}
          <div className="flex justify-center mb-10 md:mb-12">
            <Link
              to="/contract"
              data-testid="contract-download-link"
              className="inline-flex items-center gap-2 md:gap-3 bg-[#0A2540] text-white hover:bg-[#163859] transition-all duration-300 font-bold rounded-full px-5 md:px-8 py-3 md:py-4 shadow-lg hover:shadow-xl text-sm md:text-base"
            >
              <FileText className="w-4 h-4 md:w-5 md:h-5" />
              下載合約報價單（PDF）
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
            </Link>
          </div>

          {/* 三階梯主力方案 */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-12 items-stretch">
            <PlanCard
              id="pricing-card-4"
              accent="#0284C7"
              gradient="linear-gradient(90deg,#0EA5E9,#0284C7)"
              badge="入門 · 新開業推薦"
              limited={`限量體驗價 · 剩 ${LIGHT_TRIAL_LEFT} 位`}
              title="輕量方案"
              positionMode="fit"
              position="適合：新開業第 1 年 · 月營收 40 萬以下"
              price="38,000"
              stats={lightStats}
              feats={lightDiff}
              note="不推薦月營收已達 40 萬以上業主 — 25% 抽成會多付"
              cta="pricing-cta-4"
            />
            <PlanCard
              id="pricing-card-5"
              popular
              accent="#0D9488"
              gradient="linear-gradient(90deg,#14B8A6,#0D9488)"
              badge="中階 · 最佳性價比"
              limited="限量前 10 位"
              title="人客多成長方案"
              positionMode="star"
              position="每 5 位業主有 4 位選擇 · 月營收 40–90 萬"
              price="56,000"
              original="66,000"
              saveTag="省 1 萬"
              stats={growthStats}
              feats={growthDiff}
              cta="pricing-cta-5"
            />
            <PlanCard
              id="pricing-card-1"
              accent="#FF6B00"
              gradient="linear-gradient(90deg,#EF4444,#FF6B00)"
              badge="高階 · 多棟業主專屬"
              title="旗艦方案"
              positionMode="fit"
              position="適合：同地點 2 棟以上／高庫存 · 月營收 90 萬以上"
              price="78,000"
              original="108,000"
              saveTag="28% OFF"
              stats={flagshipStats}
              feats={flagshipDiff}
              extra={<FlagshipMultiUnit />}
              cta="pricing-cta-1"
            />
          </div>

          {/* 合約期：用陪跑的方式強調 */}
          <div data-testid="contract-period-note" className="-mt-4 mb-14 md:mb-20 flex justify-center">
            <div className="inline-flex items-start md:items-center gap-3 bg-[#0A2540] text-white rounded-2xl md:rounded-full px-5 md:px-6 py-3.5 shadow-lg max-w-4xl">
              <CalendarCheck className="w-5 h-5 text-[#F5C24A] flex-shrink-0 mt-0.5 md:mt-0" />
              <div className="text-sm md:text-base leading-snug">
                <span className="font-black text-[#F5C24A]">三方案皆為 12 個月陪跑合約</span>
                <span className="text-white/80"> — 行銷成效需要走完一個完整的淡旺季週期，我們陪您把每個月都走穩。</span>
              </div>
            </div>
          </div>

          {/* 同樣一筆錢：一位員工 vs 一整個行銷部門 */}
          <TeamValueSection />

          {/* 客服黃金時段真實數據洞察 */}
          <PrimeTimeInsight />

          {/* 三方案完整比較表 */}
          <ComparisonTable />

          {/* 彈性合作模式（保障分潤 + 回租，折疊呈現） */}
          <div className="mb-12">
            <div className="flex justify-center mb-6">
              <button
                type="button"
                onClick={() => setFlexOpen(!flexOpen)}
                data-testid="flex-models-toggle"
                className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 transition-colors rounded-full px-5 py-3 text-sm font-bold text-[#0A2540]"
              >
                另有兩種彈性合作模式：保障分潤（0 月費）、回租（我們付您租金）
                <ChevronDown className={`w-5 h-5 transition-transform ${flexOpen ? "rotate-180" : ""}`} />
              </button>
            </div>
            {flexOpen && (
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {/* 保障分潤方案 */}
              <div data-testid="pricing-card-2" className="bg-white rounded-3xl shadow-lg border-2 border-[#FF6B00]/30 p-6 md:p-8">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="inline-flex items-center gap-1 bg-[#FF6B00] text-white text-xs font-bold px-3 py-1 rounded-full">0 月費</span>
                  <span className="text-[11px] text-[#475569]">低門檻啟動，共享成果</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-[#0A2540] mb-1">保障分潤方案</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="text-sm text-[#475569]">NT$</span>
                  <span className="text-3xl md:text-4xl font-black text-[#FF6B00]">0</span>
                  <span className="text-sm text-[#475569]">月費 · 僅一次性啟動費 NT$50,000</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                  {["約定保障營業額（不抽成）", "超過保障額五五分成", "各自負擔各自成本", "完整核心服務"].map((t) => (
                    <div key={t} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#FF6B00] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#475569]">{t}</span>
                    </div>
                  ))}
                </div>
                <a href={LINE_URL} target="_blank" rel="noopener noreferrer" data-testid="pricing-cta-2"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-bold bg-[#0A2540] text-white hover:bg-[#163859] transition-all">
                  <MessageCircle className="w-5 h-5" />立即諮詢
                </a>
              </div>

              {/* 回租方案 */}
              <div data-testid="pricing-card-3" className="relative bg-gradient-to-br from-[#0A2540] to-[#163859] rounded-3xl p-6 md:p-8 shadow-lg">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="inline-flex items-center gap-1 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    <ArrowRight className="w-4 h-4 rotate-180" />我們付您
                  </span>
                  <span className="text-[11px] text-white/60">穩定收租，完全免操心</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1">回租方案</h3>
                <div className="flex items-baseline gap-1 flex-wrap mb-4">
                  <span className="text-sm text-white/70">NT$</span>
                  <span className="text-3xl md:text-4xl font-black text-white">35,000</span>
                  <span className="text-base text-white/70">~</span>
                  <span className="text-3xl md:text-4xl font-black text-white">120,000</span>
                  <span className="text-sm text-white/50">/ 月（依物件評估）</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                  {["每月固定租金收入", "營運風險我方全額承擔", "管家人事我方負擔", "物業水電我方負擔"].map((t) => (
                    <div key={t} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-white/90">{t}</span>
                    </div>
                  ))}
                </div>
                <a href={LINE_URL} target="_blank" rel="noopener noreferrer" data-testid="pricing-cta-3"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-bold bg-green-500 text-white hover:bg-green-600 transition-all">
                  <MessageCircle className="w-5 h-5" />立即諮詢
                </a>
              </div>
            </div>
            )}
          </div>

          {/* 底部優惠橫幅：多棟 + 早鳥（並列） */}
          <div className="mt-8 md:mt-12 grid md:grid-cols-2 gap-4 md:gap-6">
            {/* 多棟業主優惠 */}
            <div className="bg-gradient-to-r from-[#FFF7ED] via-[#FFEDD5] to-[#FFF7ED] border-2 border-[#FF6B00]/30 rounded-2xl p-5 md:p-6 shadow-md">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 md:w-12 md:h-12 bg-[#FF6B00] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div className="text-base md:text-lg font-black text-[#0A2540]">同地點有多棟物件嗎？</div>
              </div>
              <div className="text-sm md:text-base text-[#475569] leading-relaxed mb-2">
                每多一棟，<span className="text-[#FF6B00] font-black text-lg md:text-xl">只要多 NT$ 5,000/月</span>
              </div>
              <div className="text-xs text-[#475569] mb-3">旗艦、人客多成長、輕量、保障分潤、回租方案皆適用</div>
              <a href={LINE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 bg-[#0A2540] text-white hover:bg-[#163859] transition-all font-bold rounded-full px-4 py-2 text-xs md:text-sm">
                諮詢多棟優惠<ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>

            {/* 早鳥長約特惠 */}
            <div className="bg-gradient-to-r from-[#FFF8E1] via-[#FFEDC7] to-[#FFF8E1] border-2 border-[#F5C24A]/60 rounded-2xl p-5 md:p-6 shadow-md">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 md:w-12 md:h-12 bg-gradient-to-br from-[#F5C24A] to-[#E0A030] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                    <Star className="w-6 h-6 text-[#0A2540]" />
                  </div>
                  <div className="text-base md:text-lg font-black text-[#0A2540]">早鳥長約特惠</div>
                </div>
                <div className="inline-flex items-center gap-1.5 bg-red-500/10 border border-red-500/30 rounded-full px-2.5 py-1">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  <span className="text-[10px] md:text-xs font-bold text-red-600">
                    <span className="line-through opacity-60 mr-1">限量 {EARLYBIRD_TOTAL} 位</span>
                    剩 <span className="text-sm md:text-base font-black">{EARLYBIRD_LEFT} 位</span>
                  </span>
                </div>
              </div>
              <div className="text-sm md:text-base text-[#475569] leading-relaxed mb-2">
                獨享 <span className="text-[#B8860B] font-black text-lg md:text-xl">見證場保障方案</span>
                <span className="text-[#B8860B] font-bold ml-1">（更安心更有保障）</span>
              </div>
              <div className="text-xs text-[#475569] mb-3">早鳥限量業主專屬保障條款，未達營業額門檻自動降低月費／抽成</div>
              <a href={LINE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 bg-[#B8860B] text-white hover:bg-[#9c7009] transition-all font-bold rounded-full px-4 py-2 text-xs md:text-sm">
                諮詢早鳥優惠<ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </TooltipProvider>
  );
};

export default PricingSection;
